import { CellClickedEvent } from '@ag-grid-community/core';
import { Component, OnInit } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { StateService } from '@app/core/services/state.service';
import { UtilityService } from '@app/core/services/utility.service';
import { EquipmentItem, EquipmentService } from '@app/services/equipment.service';
import { FactoryItem, FactoryService } from '@app/services/factory.service';
import { BusinesscoItem, LogisticService, OperationItem } from '@app/services/logistic.service';
import { GetShopFloorParams, ShopFloorGet, ShopFloorSelectedJobGet, ShopFloorService } from '@app/services/shop-floor.service';
import { LabourerTrItem, TimeRegisterService } from '@app/services/time-register.service';

@Component({
    selector: 'tt-shop-floor',
    templateUrl: './shop-floor.component.html',
    styleUrls: ['./shop-floor.component.css'],
})
export class ShopFloorComponent implements OnInit {
    public params: GetShopFloorParams;

    public get?: ShopFloorGet & (ShopFloorSelectedJobGet | AllNever<ShopFloorSelectedJobGet>);

    public companies: BusinesscoItem[] = [];

    public factories: FactoryItem[] = [];

    public operations: OperationItem[] = [];

    public labourers: LabourerTrItem[] = [];

    public equipments: EquipmentItem[] = [];

    public grid?: GridOptions<ShopFloorGridRow>;

    public ordersGrid?: GridOptions<ShopFloorOrdersGridRow>;

    public startingJob = false;

    constructor(
        private state: StateService,
        private logistic: LogisticService,
        private timeRegister: TimeRegisterService,
        private factory: FactoryService,
        private equipment: EquipmentService,
        private shopFloor: ShopFloorService,
        private remember: RememberService,
        private modal: ModalService,
        private utility: UtilityService,
    ) {
        this.params = state.getStateParams() as unknown as GetShopFloorParams;
    }

    public async startJob() {
        if (!this.get) return;

        try {
            this.startingJob = true;

            const response = await this.shopFloor.startShopFloorSelectedJob(this.get);

            if ('' + response.errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response.errormessage });
            } else {
                this.loadSelectedJob();

                if (!!response.goto_item_state) {
                    this.state.go(response.goto_item_state, response.goto_item_parms);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.startingJob = false;
        }
    }

    public async onBusinesscoNoChanged() {
        if (!this.get) return;

        this.loadFactories();
        await this.remember.remember('w_shopfloor.businessco_no', this.get.businessco_no);
        this.readGrid();
        this.readOrdersGrid();
    }

    public async onFactoryIdChanged() {
        if (!this.get) return;

        this.loadOperations();
        this.loadEquipments();
        await this.remember.remember('w_shopfloor.factory_id', this.get.factory_id);
        this.readGrid();
        this.readOrdersGrid();
    }

    public async onOperationIdChanged() {
        if (!this.get) return;

        this.loadEquipments();
        await this.remember.remember('w_shopfloor.operation_id', this.get.operation_id);
        this.readGrid();
        this.readOrdersGrid();
    }

    public async onEquipmentIdChanged() {
        if (!this.get) return;

        await this.remember.remember('w_shopfloor.equipment_id', this.get.equipment_id);
        this.readGrid();
        this.readOrdersGrid();
    }

    public async onLabourNoChanged() {
        if (!this.get) return;

        this.loadSelectedJob();
        await this.remember.remember('w_shopfloor.labour_no', this.get.labour_no);
        this.readGrid();
        this.readOrdersGrid();
    }

    public async onDateRangeChanged() {
        if (!this.get) return;

        this.readGrid();
        this.readOrdersGrid();
    }

    private async loadGet() {
        this.get = { ...this.get, ...(await this.shopFloor.getShopFloor(this.params)) };
    }

    private async loadSelectedJob() {
        if (!!this.get) {
            const job: ShopFloorSelectedJobGet | undefined = await this.shopFloor.getShopFloorSelectedJob(this.get);

            if (!!job) {
                this.get = { ...this.get, ...job };
            } else {
                delete this.get.order_order_no;
                delete this.get.prod_id;
                delete this.get.prod_name;
                delete this.get.show_button_start_selected_job;
                delete this.get.url_picture;
                delete this.get.stamped;
                delete this.get.stat_cust_name;
                delete this.get.stat_cust_no;
            }
        }
    }

    private async loadCompanies() {
        this.companies = await this.logistic.getCompanies();
    }

    private async loadFactories() {
        this.factories = await this.factory.getFactories({ businessco_no: this.get?.businessco_no });
    }

    private async loadOperations() {
        this.operations = await this.logistic.getOperations({ direct: '1', factory_id: this.get?.factory_id });
    }

    private async loadLabourers() {
        this.labourers = await this.timeRegister.getLaborersTr();
    }

    private async loadEquipments() {
        this.equipments = await this.equipment.getEquipments({ workgrp_keyno: '0', factory_id: this.get?.factory_id, operation_id: this.get?.operation_id });
    }

    public readGrid() {
        if (!this.grid) {
            this.grid = new Grid<ShopFloorGridRow>().setRememberId('w_shopfloor_grid').setLoadDataFunction({ method: 3681, parameters: () => this.get });
        } else {
            this.grid.gridfunc?.read();
        }
    }

    public onGridCellClick(event: CellClickedEvent<ShopFloorGridRow>) {
        if (!event.data || !this.get) return;

        this.setSelectedJob({ selected_jobid: event.data.pow_keyno, selected_jobtype: 'pow_keyno' });
    }

    public readOrdersGrid() {
        if (!this.ordersGrid) {
            this.ordersGrid = new Grid<ShopFloorOrdersGridRow>()
                .setRememberId('w_shopfloor_orders_grid')
                .setLoadDataFunction({ method: 3699, parameters: () => this.get })
                .addRowButton({
                    name: 'orderpicknew',
                    tooltip: 'orderpicknew',
                    translateTooltip: true,
                    icon: 'far fa-plus',
                    btn_type: 'primary',
                    func: async (row) => {
                        const response = await this.modal.openStateDialog({ state: 'orderpicknew', params: `order_internal_no/${row['order_internal_no']}` });

                        if (!!response) this.ordersGrid?.gridfunc?.read();
                    },
                });
        } else {
            this.ordersGrid.gridfunc?.read();
        }
    }

    public onOrdersGridCellClick(event: CellClickedEvent<ShopFloorOrdersGridRow>) {
        if (!event.data || !this.get) return;

        this.setSelectedJob({ selected_jobid: event.data.order_internal_no, selected_jobtype: 'order_internal_no' });
    }

    private async setSelectedJob(params: Pick<ShopFloorGet, 'selected_jobid' | 'selected_jobtype'>) {
        if (!this.get) return;

        this.get.selected_jobid = params.selected_jobid;
        this.get.selected_jobtype = params.selected_jobtype;

        await Promise.all([this.remember.remember('w_shopfloor.selected_jobid', this.get.selected_jobid), this.remember.remember('w_shopfloor.selected_jobtype', this.get.selected_jobtype)]);

        this.loadSelectedJob();
    }

    private async init() {
        this.loadCompanies();
        this.loadLabourers();
        await this.loadGet();
        this.loadFactories();
        this.loadOperations();
        this.loadEquipments();
        await this.loadSelectedJob();
        this.readGrid();
        this.readOrdersGrid();
    }

    ngOnInit(): void {
        this.init();
    }
}

type AllNever<T> = { [P in keyof T]?: never };

interface ShopFloorGridRow {
    item_thumb: string;
    is_selected: boolean;
    planned_date: string;
    planned_week: string;
    planned_month: string;
    operation_id: string;
    operation_name: string;
    workgrp_keyno: number;
    workgrp_name: string;
    equipment_id: string;
    equipment_name: string;
    po_keyno: number;
    hours_to_plan: number;
    order_order_no: number;
    cust_name: string;
    dimension: string;
    project_keyno: number;
    project_name: string;
    prod_id: string;
    prod_name: string;
    pow_keyno: number;
    prodorder_quantity: number;
    prodorder_quantity_finished: number;
    prodorder_quantity_remaining: number;
    prodorder_postatus_no: number;
    prodorder_postatus_name: string;
    unit_name: string;
    opstatus_keyno: number;
    opstatus_name: string;
    days_to_planned_date: number;
    orderline_prod_info: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
    stock_keyno: number;
    stock_stockvalue: number;
    stock_stockvalue_ex_assigned: number;
}

interface ShopFloorOrdersGridRow {
    order_internal_no: number;
    order_order_no: number;
    stat_cust_no: number;
    stat_cust_name: string;
    deliverydate_confirmed_from_stock: Date;
    project_keyno: number;
    project_name: string;
    nbr_of_orderpick: number;
    customers_orderno: string;
    stockplace_name: string;
    last_orderpick_reg_datetime: Date;
}
